import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { 
  Gift,
  Coffee,
  CurrencyDollar,
  Calendar,
  Smiley,
  Crown,
  CaretRight
} from "@phosphor-icons/react";
import OnboardingTutorial from "@/components/OnboardingTutorial";

// ═══════════════════════════════════════════════════════════════════════════
// FRANKY'S FARM - BEN & JERRY'S STYLE
// 2x2 Grid, Flat Illustrations, Bold Typography
// ═══════════════════════════════════════════════════════════════════════════

// Logo URL
const FRANKY_LOGO = "https://customer-assets.emergentagent.com/job_coupon-unlock-1/artifacts/d1x0jxnq_Untitled%20design%20%2857%29.png";

// Color Palette (from reference image)
const COLORS = {
  bg: "#F5E6E0",              // Warm beige/pink
  green: "#2D5A3D",           // Dark forest green
  orange: "#E07A3D",          // Rust orange
  tan: "#D4A574",             // Tan/camel
  greenLight: "#4A7C59",      // Lighter green
  text: "#1A1A1A",
  white: "#FFFFFF",
};

// Tile Data - Now with Table Reservation at top!
const TILES = [
  {
    id: "tables",
    title: "TISCH\nBUCHEN",
    color: "#8B4C70", // Deep plum/burgundy for restaurants
    icon: "table",
    route: "/home?tab=reservation",
    badge: "NEU",
  },
  {
    id: "coupons",
    title: "COUPONS\n& DEALS",
    color: COLORS.green,
    icon: "gift",
    route: "/home?tab=coupons",
  },
  {
    id: "stamps",
    title: "STEMPEL-\nKARTE",
    color: COLORS.orange,
    icon: "stamp",
    route: "/home?tab=stamps",
  },
  {
    id: "order",
    title: "ONLINE\nBESTELLEN",
    color: COLORS.tan,
    icon: "coffee",
    route: "/order",
  },
  {
    id: "invest",
    title: "MITMACHEN\n& WACHSEN",
    color: COLORS.green,
    icon: "money",
    route: "/home?tab=invest",
    badge: "COMING SOON",
  },
];

// ═══════════════════════════════════════════════════════════════════════════
// CUSTOM FLAT ILLUSTRATIONS (SVG)
// Super playful Ben & Jerry's style - Unique fun characters!
// ═══════════════════════════════════════════════════════════════════════════

// 🪑 TABLE: Cute restaurant table with chairs
const TableIllustration = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full">
    {/* Table top */}
    <ellipse cx="40" cy="35" rx="30" ry="8" fill="#E8C49A"/>
    <ellipse cx="40" cy="33" rx="28" ry="7" fill="#D4A574"/>
    {/* Table leg */}
    <rect x="37" y="40" width="6" height="25" rx="2" fill="#C25D3D"/>
    {/* Table base */}
    <ellipse cx="40" cy="68" rx="18" ry="5" fill="#E07A3D"/>
    {/* Plate */}
    <ellipse cx="30" cy="30" rx="10" ry="5" fill="#FFF"/>
    <ellipse cx="30" cy="29" rx="8" ry="4" fill="#F5F5F5"/>
    {/* Cup */}
    <rect x="48" y="24" width="10" height="12" rx="2" fill="#2D5A3D"/>
    <rect x="50" y="26" width="6" height="8" rx="1" fill="#4A7C59"/>
    <path d="M58 27 Q62 28 62 32 Q62 36 58 37" stroke="#2D5A3D" strokeWidth="2" fill="none"/>
    {/* Steam from cup */}
    <path d="M52 20 Q54 16 52 12" stroke="#888" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.5"/>
    <path d="M56 18 Q58 14 56 10" stroke="#888" strokeWidth="1.5" fill="none" strokeLinecap="round" opacity="0.5"/>
    {/* Fork */}
    <rect x="18" y="22" width="2" height="16" rx="0.5" fill="#C0C0C0"/>
    <path d="M17 22 L17 28 M19 22 L19 28 M21 22 L21 28" stroke="#C0C0C0" strokeWidth="1" strokeLinecap="round"/>
    {/* Little hearts/sparkles */}
    <path d="M8 45 C8 43 10 43 10 45 C10 43 12 43 12 45 C12 48 10 50 10 50 C10 50 8 48 8 45" fill="#FF6B6B" opacity="0.7"/>
    <path d="M68 42 L69 44 L71 44 L70 46 L70 48 L68 47 L66 48 L66 46 L65 44 L67 44 Z" fill="#FFD700"/>
    {/* Clock indicating reservation time */}
    <circle cx="68" cy="18" r="8" fill="#FFF" stroke="#2D5A3D" strokeWidth="2"/>
    <line x1="68" y1="18" x2="68" y2="14" stroke="#2D5A3D" strokeWidth="2" strokeLinecap="round"/>
    <line x1="68" y1="18" x2="72" y2="18" stroke="#E07A3D" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

// 🎫 COUPONS: Happy dancing ticket with arms & legs
const GiftIllustration = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full">
    {/* Ticket body */}
    <rect x="12" y="20" width="56" height="40" rx="6" fill="#FFF9F0"/>
    {/* Ticket notches */}
    <circle cx="12" cy="40" r="6" fill="#2D5A3D"/>
    <circle cx="68" cy="40" r="6" fill="#2D5A3D"/>
    {/* Dashed line */}
    <line x1="40" y1="22" x2="40" y2="58" stroke="#E07A3D" strokeWidth="2" strokeDasharray="4,3"/>
    {/* Percent symbol */}
    <circle cx="26" cy="35" r="8" fill="#E07A3D"/>
    <text x="26" y="39" textAnchor="middle" fontSize="12" fontWeight="bold" fill="#FFF">%</text>
    {/* Cute face */}
    <circle cx="52" cy="32" r="4" fill="#2D5A3D"/>
    <circle cx="62" cy="32" r="4" fill="#2D5A3D"/>
    <circle cx="51" cy="31" r="1.5" fill="#FFF"/>
    <circle cx="61" cy="31" r="1.5" fill="#FFF"/>
    <path d="M50 42 Q57 50 64 42" stroke="#2D5A3D" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
    {/* Rosy cheeks */}
    <ellipse cx="47" cy="38" rx="3" ry="2" fill="#FFB6C1" opacity="0.6"/>
    <ellipse cx="67" cy="38" rx="3" ry="2" fill="#FFB6C1" opacity="0.6"/>
    {/* Dancing legs */}
    <path d="M25 60 L20 75" stroke="#D4A574" strokeWidth="4" strokeLinecap="round"/>
    <path d="M35 60 L42 73" stroke="#D4A574" strokeWidth="4" strokeLinecap="round"/>
    {/* Little shoes */}
    <ellipse cx="18" cy="76" rx="5" ry="3" fill="#E07A3D"/>
    <ellipse cx="44" cy="74" rx="5" ry="3" fill="#E07A3D"/>
    {/* Waving arm */}
    <path d="M58 55 Q70 50 72 62" stroke="#D4A574" strokeWidth="4" strokeLinecap="round"/>
    <circle cx="73" cy="64" r="4" fill="#D4A574"/>
    {/* Sparkles */}
    <path d="M8 15 L10 20 L15 18 L10 22 L12 27 L8 23 L4 27 L6 22 L1 20 L6 18 Z" fill="#FFD700"/>
  </svg>
);

// ☕ STAMPS: Cute coffee bean trio friends
const StampIllustration = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full">
    {/* Background card */}
    <rect x="5" y="25" width="70" height="45" rx="8" fill="#FFF9F0"/>
    {/* Coffee Bean 1 - big */}
    <ellipse cx="25" cy="48" rx="14" ry="18" fill="#6B4423"/>
    <path d="M25 35 Q22 48 25 61" stroke="#4A2F1A" strokeWidth="3" fill="none"/>
    <circle cx="20" cy="42" r="3" fill="#FFF"/>
    <circle cx="30" cy="42" r="3" fill="#FFF"/>
    <circle cx="19" cy="41" r="1.5" fill="#2D2D2D"/>
    <circle cx="29" cy="41" r="1.5" fill="#2D2D2D"/>
    <path d="M20 52 Q25 58 30 52" stroke="#2D2D2D" strokeWidth="2" fill="none" strokeLinecap="round"/>
    {/* Coffee Bean 2 - medium */}
    <ellipse cx="50" cy="50" rx="11" ry="15" fill="#8B5A2B"/>
    <path d="M50 40 Q48 50 50 60" stroke="#6B4423" strokeWidth="2" fill="none"/>
    <circle cx="46" cy="46" r="2.5" fill="#FFF"/>
    <circle cx="54" cy="46" r="2.5" fill="#FFF"/>
    <circle cx="45" cy="45" r="1.2" fill="#2D2D2D"/>
    <circle cx="53" cy="45" r="1.2" fill="#2D2D2D"/>
    <path d="M46 53 Q50 57 54 53" stroke="#2D2D2D" strokeWidth="2" fill="none" strokeLinecap="round"/>
    {/* Coffee Bean 3 - small jumping */}
    <ellipse cx="68" cy="42" rx="8" ry="11" fill="#A0522D"/>
    <path d="M68 35 Q66 42 68 49" stroke="#8B5A2B" strokeWidth="2" fill="none"/>
    <circle cx="65" cy="39" r="2" fill="#FFF"/>
    <circle cx="71" cy="39" r="2" fill="#FFF"/>
    <path d="M65 44 Q68 47 71 44" stroke="#2D2D2D" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
    {/* Little arms waving */}
    <path d="M12 45 Q5 40 8 32" stroke="#6B4423" strokeWidth="3" strokeLinecap="round"/>
    <path d="M38 45 Q42 38 48 40" stroke="#6B4423" strokeWidth="2" strokeLinecap="round"/>
    {/* Stars & hearts */}
    <path d="M40 18 L42 23 L47 23 L43 26 L45 31 L40 28 L35 31 L37 26 L33 23 L38 23 Z" fill="#FFD700"/>
    <path d="M10 20 C10 16 16 16 16 20 C16 24 10 28 10 28 C10 28 4 24 4 20 C4 16 10 16 10 20" fill="#FF6B6B"/>
  </svg>
);

// 🍕 ORDER: Happy takeaway bag with yummy food peeking out
const CoffeeIllustration = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full">
    {/* Paper bag body */}
    <path d="M15 30 L15 70 L65 70 L65 30 L55 30 L55 25 Q55 18 40 18 Q25 18 25 25 L25 30 Z" fill="#E8C49A"/>
    {/* Bag fold lines */}
    <path d="M25 30 L25 70" stroke="#D4A574" strokeWidth="1"/>
    <path d="M55 30 L55 70" stroke="#D4A574" strokeWidth="1"/>
    {/* Cute face on bag */}
    <circle cx="32" cy="48" r="5" fill="#FFF"/>
    <circle cx="48" cy="48" r="5" fill="#FFF"/>
    <circle cx="31" cy="47" r="2.5" fill="#2D5A3D"/>
    <circle cx="47" cy="47" r="2.5" fill="#2D5A3D"/>
    <path d="M34 58 Q40 66 46 58" stroke="#2D5A3D" strokeWidth="3" fill="none" strokeLinecap="round"/>
    {/* Tongue sticking out */}
    <ellipse cx="40" cy="63" rx="4" ry="3" fill="#FF6B6B"/>
    {/* Rosy cheeks */}
    <ellipse cx="25" cy="52" rx="4" ry="2" fill="#FFB6C1" opacity="0.5"/>
    <ellipse cx="55" cy="52" rx="4" ry="2" fill="#FFB6C1" opacity="0.5"/>
    {/* Food peeking out - Croissant */}
    <path d="M30 25 Q35 15 45 18 Q50 20 48 28" fill="#E07A3D"/>
    <path d="M33 24 Q38 18 44 20" stroke="#C25D3D" strokeWidth="1" fill="none"/>
    {/* Food peeking out - Leaf/salad */}
    <ellipse cx="55" cy="22" rx="8" ry="5" fill="#4A7C59"/>
    <ellipse cx="52" cy="20" rx="5" ry="3" fill="#2D5A3D"/>
    {/* Steam/aroma */}
    <path d="M35 10 Q37 6 35 2" stroke="#2D5A3D" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.5"/>
    <path d="M45 8 Q47 4 45 0" stroke="#2D5A3D" strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.5"/>
    {/* Little legs */}
    <rect x="28" y="70" width="6" height="8" rx="2" fill="#D4A574"/>
    <rect x="46" y="70" width="6" height="8" rx="2" fill="#D4A574"/>
  </svg>
);

// 🌱 INVEST: Happy plant growing from pot with coins
const MoneyIllustration = () => (
  <svg viewBox="0 0 80 80" className="w-full h-full">
    {/* Pot */}
    <path d="M20 50 L25 75 L55 75 L60 50 Z" fill="#E07A3D"/>
    <rect x="15" y="45" width="50" height="8" rx="3" fill="#C25D3D"/>
    {/* Pot face */}
    <circle cx="32" cy="62" r="3" fill="#FFF"/>
    <circle cx="48" cy="62" r="3" fill="#FFF"/>
    <circle cx="31" cy="61" r="1.5" fill="#8B4513"/>
    <circle cx="47" cy="61" r="1.5" fill="#8B4513"/>
    <path d="M35 68 Q40 72 45 68" stroke="#8B4513" strokeWidth="2" fill="none" strokeLinecap="round"/>
    {/* Main plant stem */}
    <path d="M40 45 Q38 30 40 20" stroke="#2D5A3D" strokeWidth="4" fill="none" strokeLinecap="round"/>
    {/* Leaves */}
    <ellipse cx="30" cy="28" rx="12" ry="7" fill="#4A7C59" transform="rotate(-30 30 28)"/>
    <ellipse cx="50" cy="25" rx="10" ry="6" fill="#2D5A3D" transform="rotate(25 50 25)"/>
    <ellipse cx="35" cy="18" rx="8" ry="5" fill="#4A7C59" transform="rotate(-15 35 18)"/>
    {/* Cute flower/sprout on top */}
    <circle cx="42" cy="12" r="6" fill="#FFD700"/>
    <circle cx="40" cy="10" r="2" fill="#FFF"/>
    <circle cx="44" cy="10" r="2" fill="#FFF"/>
    <circle cx="39" cy="9" r="1" fill="#2D5A3D"/>
    <circle cx="43" cy="9" r="1" fill="#2D5A3D"/>
    <path d="M40 14 Q42 16 44 14" stroke="#2D5A3D" strokeWidth="1" fill="none"/>
    {/* Floating coins */}
    <ellipse cx="12" cy="35" rx="8" ry="5" fill="#FFD700" transform="rotate(-15 12 35)"/>
    <text x="12" y="37" textAnchor="middle" fontSize="8" fill="#E07A3D">€</text>
    <ellipse cx="68" cy="30" rx="8" ry="5" fill="#FFD700" transform="rotate(15 68 30)"/>
    <text x="68" y="32" textAnchor="middle" fontSize="8" fill="#E07A3D">€</text>
    <ellipse cx="15" cy="55" rx="6" ry="4" fill="#E8C49A"/>
    <ellipse cx="65" cy="55" rx="6" ry="4" fill="#E8C49A"/>
    {/* Sparkles */}
    <path d="M5 20 L7 24 L11 23 L7 26 L9 30 L5 27 L1 30 L3 26 L-1 24 L3 23 Z" fill="#FFD700"/>
    <path d="M72 45 L74 49 L78 48 L74 51 L76 55 L72 52 L68 55 L70 51 L66 49 L70 48 Z" fill="#FFD700"/>
  </svg>
);

const getIllustration = (icon) => {
  switch(icon) {
    case "table": return <TableIllustration />;
    case "gift": return <GiftIllustration />;
    case "stamp": return <StampIllustration />;
    case "coffee": return <CoffeeIllustration />;
    case "money": return <MoneyIllustration />;
    default: return <GiftIllustration />;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// TILE COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const Tile = ({ tile, index, onClick }) => {
  // Tiles are now always clickable, badge is just informational
  
  return (
    <motion.button
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: 0.2 + index * 0.1, duration: 0.4 }}
      whileHover={{ scale: 1.03, y: -4 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => onClick(tile)}
      className="relative aspect-square rounded-3xl p-4 flex flex-col overflow-hidden"
      style={{ backgroundColor: tile.color }}
      data-testid={`tile-${tile.id}`}
    >
      {/* Badge */}
      {tile.badge && (
        <span 
          className="absolute top-3 right-3 px-2 py-1 rounded-lg text-[10px] font-bold"
          style={{ backgroundColor: "rgba(0,0,0,0.2)", color: "#fff" }}
        >
          {tile.badge}
        </span>
      )}

      {/* Title at TOP */}
      <h3 
        className="text-lg font-black text-white leading-tight whitespace-pre-line"
        style={{ fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {tile.title}
      </h3>

      {/* Illustration at BOTTOM - ANIMATED! */}
      <div className="flex-1 flex items-end justify-center">
        <motion.div 
          className="w-28 h-28"
          animate={{ 
            y: [0, -6, 0],
            rotate: tile.icon === "gift" ? [0, -3, 3, 0] : [0, 2, -2, 0],
          }}
          transition={{ 
            duration: tile.icon === "stamp" ? 2 : tile.icon === "money" ? 2.5 : 1.8,
            repeat: Infinity,
            ease: "easeInOut",
            delay: tile.icon === "coffee" ? 0.3 : tile.icon === "money" ? 0.6 : 0,
          }}
        >
          {getIllustration(tile.icon)}
        </motion.div>
      </div>
    </motion.button>
  );
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN LANDING PAGE
// ═══════════════════════════════════════════════════════════════════════════
export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const location = localStorage.getItem("frankysfarm_location") || "mannheim";
  
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    setIsInstalled(isStandalone);
    const iosDevice = /iPad|iPhone|iPod/.test(navigator.userAgent);
    setIsIOS(iosDevice);
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallApp = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      setDeferredPrompt(null);
    }
  };
  
  useEffect(() => {
    // Onboarding moved to after registration
    if (false) {
      const timer = setTimeout(() => setShowOnboarding(true), 800);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleTileClick = (tile) => {
    navigate(tile.route);
  };

  return (
    <div 
      className="min-h-screen pb-24 relative"
      style={{ backgroundColor: COLORS.bg }}
    >
      {/* Onboarding */}
      {showOnboarding && (
        <OnboardingTutorial onComplete={() => setShowOnboarding(false)} />
      )}

      {/* Header */}
      <header className="relative z-10 px-5 pt-6 pb-2">
        <div className="flex items-start justify-between">
          {/* Logo */}
          <motion.img
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            src={FRANKY_LOGO}
            alt="Franky's Farm"
            className="w-20 h-20 object-contain"
          />
          
          {/* Smiley Badge - ANIMATED with blinking! - CLICKABLE */}
          <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3, type: "spring" }}
            whileTap={{ scale: 0.9 }}
            onClick={() => {
              if (user) {
                navigate("/profile");
              } else {
                // Trigger auth modal via custom event
                document.dispatchEvent(new CustomEvent('show-auth-modal'));
              }
            }}
            className="w-12 h-12 rounded-full flex items-center justify-center relative overflow-hidden cursor-pointer"
            style={{ backgroundColor: COLORS.orange }}
            data-testid="smiley-auth-button"
          >
            {/* Animated Blinking Smiley SVG */}
            <svg viewBox="0 0 40 40" className="w-8 h-8">
              {/* Face */}
              <circle cx="20" cy="20" r="18" fill="white"/>
              
              {/* Left Eye - Blinking */}
              <motion.ellipse 
                cx="13" 
                cy="16" 
                rx="3"
                ry="3"
                fill="#2D5A3D"
                animate={{ 
                  ry: [3, 0.5, 3],
                }}
                transition={{ 
                  duration: 0.15,
                  repeat: Infinity,
                  repeatDelay: 3,
                  times: [0, 0.5, 1]
                }}
              />
              
              {/* Right Eye - Blinking */}
              <motion.ellipse 
                cx="27" 
                cy="16" 
                rx="3"
                ry="3"
                fill="#2D5A3D"
                animate={{ 
                  ry: [3, 0.5, 3],
                }}
                transition={{ 
                  duration: 0.15,
                  repeat: Infinity,
                  repeatDelay: 3,
                  times: [0, 0.5, 1]
                }}
              />
              
              {/* Big Smile */}
              <path 
                d="M10 24 Q20 34 30 24" 
                stroke="#2D5A3D" 
                strokeWidth="3" 
                fill="none" 
                strokeLinecap="round"
              />
              
              {/* Rosy Cheeks */}
              <circle cx="8" cy="22" r="3" fill="#FFB6C1" opacity="0.6"/>
              <circle cx="32" cy="22" r="3" fill="#FFB6C1" opacity="0.6"/>
            </svg>
          </motion.button>
        </div>
      </header>

      {/* Welcome Text */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="px-5 pb-6"
      >
        <h1 
          className="text-4xl font-black leading-tight"
          style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {user ? `Schön, dass du da bist,\n${user.name}!` : `Willkommen\nbei Franky's!`}
        </h1>
        <p 
          className="text-xl font-bold mt-1"
          style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
        >
          {user ? `${user.points || 0} Punkte!` : `Matcha, Coffee & ganz viel Liebe`}
        </p>
      </motion.div>

      {/* Tile Grid - First 4 tiles in 2x2, last tile full width */}
      <main className="px-5">
        <div className="grid grid-cols-2 gap-4">
          {TILES.slice(0, 4).map((tile, index) => (
            <Tile 
              key={tile.id}
              tile={tile}
              index={index}
              onClick={handleTileClick}
            />
          ))}
        </div>
        
        {/* Last tile - Invest - Full width */}
        {TILES[4] && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => handleTileClick(TILES[4])}
            className="w-full mt-4 h-24 rounded-3xl p-4 flex items-center justify-between overflow-hidden relative"
            style={{ backgroundColor: TILES[4].color }}
          >
            <div className="flex items-center gap-4 z-10">
              <div className="w-16 h-16">
                <MoneyIllustration />
              </div>
              <div className="text-left">
                <h3 
                  className="text-lg font-black text-white"
                  style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                >
                  MITMACHEN & WACHSEN
                </h3>
                <p className="text-sm text-white/70">
                  Hilf uns, Aachen noch schöner zu machen
                </p>
              </div>
            </div>
            <span 
              className="px-3 py-1.5 rounded-lg text-sm font-bold z-10"
              style={{ backgroundColor: "rgba(0,0,0,0.2)", color: "#fff" }}
            >
              BALD
            </span>
          </motion.button>
        )}

        {/* ═══════════════════════════════════════════════════════════════════
            MEMBERSHIP SECTION - Premium, eye-catching
        ═══════════════════════════════════════════════════════════════════ */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-8"
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Crown size={24} weight="fill" style={{ color: COLORS.orange }} />
              <h2 
                className="text-xl font-black"
                style={{ color: COLORS.text, fontFamily: "'Space Grotesk', sans-serif" }}
              >
                MEMBER WERDEN
              </h2>
            </div>
            <motion.span
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="px-3 py-1 rounded-full text-xs font-bold"
              style={{ backgroundColor: COLORS.orange, color: "#fff" }}
            >
              SPARE BIS 50%
            </motion.span>
          </div>

          {/* Membership Plans */}
          <div className="space-y-3">
            {/* MATCHA Plan - Featured */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/membership")}
              className="w-full rounded-2xl p-4 text-left relative overflow-hidden"
              style={{ 
                background: "linear-gradient(135deg, #7DB95A 0%, #4A7C59 100%)",
                boxShadow: "0 8px 24px rgba(77, 124, 89, 0.3)",
              }}
            >
              {/* Floating elements */}
              <motion.div
                className="absolute -right-4 -top-4 text-6xl opacity-10"
                animate={{ rotate: [0, 10, 0], y: [0, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
              >
                🍵
              </motion.div>
              <motion.div
                className="absolute right-12 bottom-2 text-2xl opacity-20"
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                🍃
              </motion.div>
              
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: "rgba(255,255,255,0.2)" }}
                  >
                    <motion.span 
                      className="text-2xl"
                      animate={{ rotate: [0, 5, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      🍵
                    </motion.span>
                  </div>
                  <div>
                    <p className="text-xs text-white/70 font-semibold uppercase tracking-wider">Beliebteste Wahl</p>
                    <p className="text-lg font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      Matcha Club
                    </p>
                    <p className="text-sm text-white/80">1x Matcha wöchentlich + 20% Rabatt</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black text-white">29,90€</p>
                  <p className="text-xs text-white/70">/Monat</p>
                </div>
              </div>
              {/* Popular Badge */}
              <div 
                className="absolute top-0 right-0 px-3 py-1 rounded-bl-xl text-[10px] font-bold"
                style={{ backgroundColor: "#FFD700", color: "#1A1A1A" }}
              >
                ⭐ BESTSELLER
              </div>
            </motion.button>

            {/* COFFEE Plan */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/membership")}
              className="w-full rounded-2xl p-4 text-left relative overflow-hidden"
              style={{ 
                backgroundColor: COLORS.green,
                boxShadow: "0 6px 20px rgba(45, 90, 61, 0.25)",
              }}
            >
              {/* Floating elements */}
              <motion.div
                className="absolute -right-2 -top-2 text-5xl opacity-10"
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                ☕
              </motion.div>
              <motion.div
                className="absolute right-16 bottom-1 text-xl opacity-15"
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 2.5, repeat: Infinity }}
              >
                ✨
              </motion.div>
              
              <div className="flex items-center justify-between relative z-10">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: "rgba(255,255,255,0.15)" }}
                  >
                    <motion.span 
                      className="text-2xl"
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    >
                      ☕
                    </motion.span>
                  </div>
                  <div>
                    <p className="text-lg font-black text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      Coffee Pass
                    </p>
                    <p className="text-sm text-white/80">1x Kaffee-Getränk täglich</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-black text-white">19,90€</p>
                  <p className="text-xs text-white/70">/Monat</p>
                </div>
              </div>
            </motion.button>
          </div>

          {/* Benefits Teaser */}
          <div className="mt-4 flex flex-wrap gap-2 justify-center">
            {["✓ Jederzeit kündbar", "✓ Sofort starten", "✓ +50% Punkte"].map((benefit, i) => (
              <span 
                key={i}
                className="px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ backgroundColor: COLORS.green + "15", color: COLORS.green }}
              >
                {benefit}
              </span>
            ))}
          </div>

          
        </motion.div>
      

      </main>
    </div>
  );
}
